/**
 * Standardized z-index values for layering across the application.
 *
 * The scale as the shared components actually render it:
 * - 0-99: page content and the dialog tier. Sticky headers sit at 10-20. Chat
 *   windows docked along the bottom-right corner sit at 25 (`dockedWindow`),
 *   under the navigation dropdowns (30-50). The shared Modal and Sidebar render
 *   at 50 (`dialog`, a Headless UI Dialog), a non-modal Sidebar at 40
 *   (`nonModalSidebar`).
 * - 100: inline dropdown menus (the shared Dropdown).
 * - 1000: select menus. Select and Typeahead portal theirs to <body>, so an
 *   open menu clears a Modal or Sidebar; Combobox renders its menu in place.
 * - 2001 and up: OverlayService floating windows (the AI assistant, chat
 *   pop-outs), one step per window by stacking rank. See
 *   `rankOverlayWindowZIndexes`.
 * - 3000: Popover. The mobile FilterTray uses 3000 for its backdrop and 3001
 *   for the tray.
 * - 4000: Tooltip.
 * - 5000-5200: notifications and toasts.
 * - 9999: react-select menus (SingleSelect, MultiSelect), SelectSearch, and
 *   `usePopover` panels such as the DatePicker, along with many hand-rolled
 *   overlays in the modules. Those are literals in their own files rather than
 *   values read from here.
 *
 * `menu`, `select` and `autocomplete` are not read by any component; the real
 * menu values are the literals listed above.
 *
 * Nesting. A flat scale cannot express "this select is inside that popover":
 * a Select menu at 1000 opened inside a Popover at 3000 rendered underneath
 * the popover and could not be used (PD-16154). Each shared overlay therefore
 * tells the overlays rendered inside it what z-index it sits at (web-ui's
 * `LayerBoundary`), and each overlay resolves its own value through
 * `resolveNestedZIndex`: at the top level it keeps its value from this scale,
 * and nested it rises to at least `LAYER_NEST_STEP` above its parent. Nothing
 * is ever lowered, so an overlay that is not nested renders exactly as the
 * scale says.
 */

export const Z_INDEX = {
	// Base layers (0-99)
	base: 0,
	elevated: 1,
	sticky: 10,
	stickyHeader: 15,
	tableHeader: 20,
	// OverlayService windows docked in the bottom-right row (the chat
	// pop-outs, PD-14656). They share that corner with right-side editor
	// sidebars and the top-nav dropdowns, so they sit below those, the way a
	// chat dock sits under dialogs in Gmail or Teams: opening an editor or a
	// menu covers the chats and closing it shows them again. A window dragged
	// out of the row goes back to the floating-window tier (2001 and up).
	dockedWindow: 25,
	// The shared Modal and the modal Sidebar (a Headless UI Dialog), and the
	// non-modal Sidebar, which floats over the page without a backdrop.
	nonModalSidebar: 40,
	dialog: 50,

	// Interactive elements (100-999)
	dropdown: 100,
	dropdownBackdrop: 99,
	menu: 150,
	select: 200,
	autocomplete: 250,

	// Overlays and modals (1000-2999)
	modalBackdrop: 1000,
	modal: 2000,
	modalContent: 2100,

	// Floating elements (3000-3999)
	popover: 3000,
	popoverBackdrop: 2999,
	floatingPanel: 3100,

	// Tooltips (4000-4999)
	tooltip: 4000,

	// Notifications (5000-5999)
	notification: 5000,
	toast: 5100,
	alert: 5200,

	// System-level (6000-9999)
	systemModal: 6000,
	criticalOverlay: 7000,
	debugPanel: 8000,

	// Maximum (use sparingly)
	maximum: 10000,
} as const;

// Type for z-index values
export type ZIndexValue = (typeof Z_INDEX)[keyof typeof Z_INDEX];

// Helper function to get z-index with offset
export function getZIndex(layer: keyof typeof Z_INDEX, offset = 0): number {
	return Z_INDEX[layer] + offset;
}

/**
 * How far above its parent a nested overlay is lifted. Ten rather than one so
 * that an overlay's own backdrop, which sits one below the overlay, still
 * clears the parent it opened from.
 */
export const LAYER_NEST_STEP = 10;

/**
 * The z-index an overlay should render at, given the z-index of the overlay it
 * is nested in (`null` when it is not nested in one).
 *
 * Not nested, the overlay keeps its own value. Nested, it takes whichever is
 * higher: its own value, or `LAYER_NEST_STEP` above the parent. The result is
 * never lower than the overlay's own value, so this can only lift an overlay
 * that would otherwise have rendered underneath the thing it was opened from.
 */
export function resolveNestedZIndex(
	ownZIndex: number,
	parentZIndex: number | null,
): number {
	if (parentZIndex == null) {
		return ownZIndex;
	}
	return Math.max(ownZIndex, parentZIndex + LAYER_NEST_STEP);
}

/**
 * The highest z-index an OverlayService floating window is given. It leaves a
 * nested layer's worth of room below the popover tier, so a window and the
 * menus opened inside it never reach a popover.
 */
export const OVERLAY_WINDOW_MAX_Z_INDEX = Z_INDEX.popover - LAYER_NEST_STEP - 1;

/**
 * Z-indexes for OverlayService's floating windows, given their ids in stacking
 * order from bottom to top. The window at rank r (1 = bottom) sits at
 * `Z_INDEX.modal + r`.
 *
 * The values come from the windows' order rather than from a counter. A
 * counter only grows, so it either climbs through the popover and tooltip
 * tiers or has to wrap back to the bottom, and a window raised just after a
 * wrap dropped beneath the windows it had been raised above. Ranking the
 * visible windows afresh on every change keeps the values between 2001 and
 * the number of open windows, whatever the history.
 */
export function rankOverlayWindowZIndexes(
	idsBottomToTop: ReadonlyArray<string>,
): Map<string, number> {
	const zIndexes = new Map<string, number>();
	idsBottomToTop.forEach((id, index) => {
		zIndexes.set(
			id,
			Math.min(Z_INDEX.modal + index + 1, OVERLAY_WINDOW_MAX_Z_INDEX),
		);
	});
	return zIndexes;
}
